<div class="bai-thread">

  <!-- ===== Controls bar ===== -->
  <div class="bai-thread__controls">

    <!-- Filter dropdown — left -->
    <div class="bai-thread__filter-select-wrap">
      <span class="bai-thread__filter-label">Filter by:</span>
      <p-select
        [options]="filterChips"
        [ngModel]="activeFilter()"
        (ngModelChange)="setFilter($event)"
        optionLabel="label"
        optionValue="id"
        styleClass="bai-thread-select"
        aria-label="Filter messages"
      />
    </div>

    <!-- View mode toggle — right -->
    <div class="bai-thread__view-toggle" role="group" aria-label="View mode">
      @for (opt of viewModeOptions; track opt.id) {
        <button
          type="button"
          class="bai-thread__view-btn"
          [class.bai-thread__view-btn--active]="viewMode() === opt.id"
          (click)="setViewMode(opt.id)"
          [attr.aria-pressed]="viewMode() === opt.id"
        >{{ opt.label }}</button>
      }
    </div>

    <!-- Invite FA button -->
    <button
      type="button"
      class="bai-thread__invite-btn"
      (click)="openInviteModal()"
      aria-label="Invite a financial assistant to this conversation"
      pTooltip="Invite FA"
      tooltipPosition="bottom"
    >
      <i class="pi pi-user-plus" aria-hidden="true"></i>
      Invite FA
    </button>

  </div>

  <!-- Invite FA modal (rendered in-place, positioned fixed via CSS) -->
  @if (showInviteModal() && sessionId()) {
    <bai-fa-invite-modal
      [sessionId]="sessionId()!"
      [inviterName]="inviterName()"
      (close)="closeInviteModal()"
      (inviteSent)="onInviteSent($event)"
    />
  }

  <!-- ===== Timeline ===== -->
  <div #threadScrollContainer class="bai-thread__scroll" aria-live="polite" aria-label="Conversation">
    <div class="bai-thread__timeline">

      @if (isLoading()) {
        <div class="bai-thread__state-msg">
          <i class="pi pi-spin pi-spinner"></i> Loading conversation…
        </div>
      }

      @if (isEmpty()) {
        <div class="bai-thread__state-msg bai-thread__state-msg--empty">
          <i class="pi pi-comments"></i>
          <p>No messages yet.</p>
        </div>
      }

      @for (item of groupedEvents(); track item.event.id) {
        @switch (item.event.eventType) {

          <!-- Client message — left bubble -->
          @case ('client_message') {
            <div class="bai-thread__row bai-thread__row--left">
              @if (item.showHeader) {
                <div class="bai-thread__avatar bai-thread__avatar--client">
                  {{ getInitials(clientDisplayName(item.event)) }}
                </div>
              } @else {
                <div class="bai-thread__avatar-spacer"></div>
              }
              <div class="bai-thread__bubble-col">
                @if (item.showHeader) {
                  <div class="bai-thread__speaker-label">
                    {{ clientDisplayName(item.event) }}
                    <span class="bai-thread__time">{{ formatTime(item.event.createdAt) }}</span>
                  </div>
                }
                <div class="bai-thread__bubble bai-thread__bubble--client">
                  <span class="bai-thread__bubble-text">{{ item.event.content }}</span>
                  <button
                    type="button"
                    class="bai-thread__quick-action"
                    (click)="addEventToCopilot(item.event)"
                    aria-label="Add client message to Copilot"
                    title="Add to Copilot"
                  >
                    <i class="pi pi-plus-circle" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          }

          <!-- Human FA message — right bubble with typewriter -->
          @case ('assistant_message_human') {
            <div class="bai-thread__row bai-thread__row--right">
              <div class="bai-thread__bubble-col bai-thread__bubble-col--right">
                @if (item.showHeader) {
                  <div class="bai-thread__speaker-label bai-thread__speaker-label--right">
                    <span class="bai-thread__time">{{ formatTime(item.event.createdAt) }}</span>
                    {{ humanDisplayName(item.event) }}
                    <span class="bai-thread__role-badge bai-thread__role-badge--human">FA</span>
                  </div>
                }
                <div class="bai-thread__bubble bai-thread__bubble--human">
                  <span class="bai-thread__bubble-text">
                    {{ item.event.renderedContent }}@if (!item.event.typingComplete) {<span class="bai-thread__cursor" aria-hidden="true">|</span>}
                  </span>
                  <button
                    type="button"
                    class="bai-thread__quick-action"
                    (click)="addEventToCopilot(item.event)"
                    aria-label="Add financial assistant message to Copilot"
                    title="Add to Copilot"
                  >
                    <i class="pi pi-plus-circle" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              @if (item.showHeader) {
                <div class="bai-thread__avatar bai-thread__avatar--human">
                  {{ getInitials(humanDisplayName(item.event)) }}
                </div>
              } @else {
                <div class="bai-thread__avatar-spacer"></div>
              }
            </div>
          }

          <!-- AIFA message — right bubble, AI icon -->
          @case ('assistant_message_ai') {
            <div class="bai-thread__row bai-thread__row--right">
              <div class="bai-thread__bubble-col bai-thread__bubble-col--right">
                @if (item.showHeader) {
                  <div class="bai-thread__speaker-label bai-thread__speaker-label--right">
                    <span class="bai-thread__time">{{ formatTime(item.event.createdAt) }}</span>
                    {{ item.event.speakerDisplayName || 'AIFA' }}
                    <span class="bai-thread__role-badge bai-thread__role-badge--ai">AI</span>
                  </div>
                }
                <div class="bai-thread__bubble bai-thread__bubble--ai">
                  <span class="bai-thread__bubble-text">{{ item.event.content }}</span>
                  <button
                    type="button"
                    class="bai-thread__quick-action"
                    (click)="addEventToCopilot(item.event)"
                    aria-label="Add AI message to Copilot"
                    title="Add to Copilot"
                  >
                    <i class="pi pi-plus-circle" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              @if (item.showHeader) {
                <div class="bai-thread__avatar bai-thread__avatar--ai">
                  <i class="pi pi-bolt"></i>
                </div>
              } @else {
                <div class="bai-thread__avatar-spacer"></div>
              }
            </div>
          }

          <!-- System handoff — centered card -->
          @case ('system_handoff_event') {
            <div class="bai-thread__handoff-card">
              <i class="pi pi-arrow-right-arrow-left"></i>
              <span>{{ item.event.content }}</span>
              <span class="bai-thread__time">{{ formatTime(item.event.createdAt) }}</span>
              <button
                type="button"
                class="bai-thread__quick-action"
                (click)="addEventToCopilot(item.event)"
                aria-label="Add handoff event to Copilot"
                title="Add to Copilot"
              >
                <i class="pi pi-plus-circle" aria-hidden="true"></i>
              </button>
            </div>
          }

          <!-- Internal note — yellow sticky -->
          @case ('conversation_note') {
            <div class="bai-thread__note-card">
              <div class="bai-thread__note-header">
                <span class="bai-thread__note-label">
                  <i class="pi pi-pencil"></i> Internal Note
                </span>
                <span class="bai-thread__note-internal-badge">Internal only</span>
                <span class="bai-thread__time">{{ formatTime(item.event.createdAt) }}</span>
              </div>
              @if (item.event.speakerDisplayName) {
                <div class="bai-thread__note-author">
                  {{ item.event.speakerDisplayName }}
                  @if (item.event.speakerRoleLabel) {
                    <span class="bai-thread__note-role"> · {{ item.event.speakerRoleLabel }}</span>
                  }
                </div>
              }
              <div class="bai-thread__note-body">{{ item.event.content }}</div>
              <button
                type="button"
                class="bai-thread__quick-action"
                (click)="addEventToCopilot(item.event)"
                aria-label="Add note to Copilot"
                title="Add to Copilot"
              >
                <i class="pi pi-plus-circle" aria-hidden="true"></i>
              </button>
            </div>
          }

        }
      }

    </div>
  </div>

</div>
